import { SessionRecorder } from './Tracium';

const getEnvironment = (): string => {
	try {
		const hostname = window.location?.hostname || '';
		if (hostname.includes('dev.spheeresint.com') || hostname.includes('dev.cyberdictionary.com') || hostname.includes('dev-demo.spheeresint.com'))
			return 'development';
		if (hostname.includes('spheeresacc.com'))
			return 'acceptance';
		if (hostname.includes('spheeresint.com') || hostname.includes('sphprd.com'))
			return 'production';
	} catch (e) {
		console.error('Error detecting environment:', e);
	}
	return 'development';
};

// Host where recording is available ON DEMAND only: it never auto-starts, but a
// manual "Record" button (see SessionRecorderButton) can turn it on. Kept
// separate from isRecordingEnabled so the auto-start path stays off here.
const isOnDemandHost = (): boolean => {
	try {
		return (window.location?.hostname || '').includes('dev.sphprd.com');
	} catch {
		return false;
	}
};

const isRecordingEnabled = (): boolean => {
	try {
		const hostname = window.location?.hostname || '';
		// Recording is disabled on these development hosts; enabled everywhere else.
		if (hostname.includes('dev.spheeresint.com') || hostname.includes('dev.cyberdictionary.com') || hostname.includes('dev.sphprd.com'))
			return false;
		// Disabled on local / e2e-test hosts: the collector is unreachable there,
		// and the failing calls stall page loads during Playwright runs.
		if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local'))
			return false;
		return true;
	} catch {
		return false;
	}
};

// Actual init, shared by the auto-start and on-demand paths. Idempotent.
const doInit = (userId?: string): typeof SessionRecorder | null => {
	if (SessionRecorder.isInitialized()) return SessionRecorder;

	try {
		SessionRecorder.init({
			collectorUrl: 'https://tracium.sphprd.com',
			apiKey: 'proj_q0JiRKE26rdcm1TnY3RWEaRskMPF7LE',
			userId,
			// Console logs only on development hosts; acceptance and production stay quiet.
			debug: getEnvironment() === 'development',
		});
	} catch (e) {
		console.error('Failed to initialize Session Recorder:', e);
		return null;
	}

	return SessionRecorder;
};

export const initSessionRecorder = (userId?: string): typeof SessionRecorder | null => {
	if (!isRecordingEnabled()) return null;
	return doInit(userId);
};

// Force recording on regardless of the auto-start gate. Only honoured on the
// on-demand host, so it can never turn recording on for local / e2e runs.
export const startOnDemandRecording = (userId?: string): typeof SessionRecorder | null => {
	if (!isOnDemandHost()) return null;
	return doInit(userId);
};

export const stopSessionRecorder = (): void => {
	if (SessionRecorder.isInitialized()) SessionRecorder.stop();
};

// Cancel: tear down without a final flush and delete the session on the
// collector (nothing is kept). Only honoured on the on-demand host.
export const discardSessionRecording = (): void => {
	if (!isOnDemandHost()) return;
	if (SessionRecorder.isInitialized()) SessionRecorder.discard();
};

export const isSessionRecording = (): boolean => SessionRecorder.isInitialized();

export const isOnDemandRecordingHost = (): boolean => isOnDemandHost();

export const getSessionRecorder = (): typeof SessionRecorder | null => {
	return SessionRecorder.isInitialized() ? SessionRecorder : null;
};
